import { useCallback, useMemo } from 'react'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import CampaignLink from '../shared/CampaignLink'
import AdGroupLink from '../shared/AdGroupLink'
import {
  addActives,
  addExpandCampaignType,
  fetchTreeAdGroups,
  removeActives,
  toggleCampaignType,
} from './dashboardTreeSlice'
import PropTypes from 'prop-types'
import { CampaignTypeNames, CampaignTypes } from '../../Codes'
import NoneAnchor from '../../../components/NoneAnchor'
import { setCampaignTypeFilter } from '../filter/dashboardFilterSlice'
import { FilterMenus } from '../filter/FilterSpecs'
import { useDashboardTableSelector } from '../utils/hooks/useDashboardTableSelector'

const CampaignList = ({ campaignType }) => {
  const dispatch = useDispatch()
  const {
    filter: { filters },
    tree: {
      campaigns,
      actives,
      expandCampaignTypes,
      adGroupLoadings,
      selectedCampaignId,
      selectedAdgroupId,
      adGroups,
    },
  } = useSelector(state => state.dashboard, shallowEqual)
  const { isLoading } = useDashboardTableSelector()
  const isExpanded = useMemo(
    () => expandCampaignTypes.includes(campaignType),
    [expandCampaignTypes, campaignType]
  )
  const filteredCampaigns = useMemo(
    () => campaigns.filter(({ type }) => type === campaignType),
    [campaigns, campaignType]
  )
  const isActive = useMemo(() => {
    if (selectedCampaignId || selectedAdgroupId) {
      return false
    } else {
      const { domain } = FilterMenus.campaign
      const key = 'type'
      const campaignTypeFilter = filters.find(
        filter =>
          filter.domain === domain &&
          filter.key === key &&
          filter.data.values.length === 1 &&
          filter.data.values[0] === campaignType
      )
      return campaignTypeFilter !== undefined
    }
  }, [selectedCampaignId, selectedAdgroupId, campaignType, filters])
  const handleCampaignTypeClick = useCallback(() => {
    batch(() => {
      dispatch(addExpandCampaignType(campaignType))
      dispatch(setCampaignTypeFilter(campaignType))
    })
  }, [dispatch, campaignType])
  const handleCampaignTypeToggle = useCallback(
    event => {
      event.stopPropagation()
      dispatch(toggleCampaignType(campaignType))
    },
    [dispatch, campaignType]
  )
  const handleCampaignToggle = useCallback(
    (ev, toggleId) => {
      ev.preventDefault()
      ev.stopPropagation()
      if (actives.includes(toggleId)) {
        dispatch(removeActives(toggleId))
      } else {
        if (adGroupLoadings[toggleId] === undefined) {
          dispatch(fetchTreeAdGroups(toggleId))
        } else {
          dispatch(addActives(toggleId))
        }
      }
    },
    [dispatch, actives, adGroupLoadings]
  )
  return (
    <>
      <li
        className={classNames({
          open: isExpanded,
          on: isActive,
        })}>
        <NoneAnchor
          className="link_dashboard"
          onClick={handleCampaignTypeClick}
          style={{
            ...(isLoading && { pointerEvents: 'none' }),
          }}>
          {CampaignTypeNames[campaignType]}
          <span
            className="ico_gnb ico_arrow"
            onClick={handleCampaignTypeToggle}
          />
        </NoneAnchor>
        {isExpanded && (
          <ul className="list_link">
            {filteredCampaigns.map(
              ({
                id: campaignId,
                name: campaignName,
                status: campaignStatus,
                adGroupIds,
                adGroupCnt,
              }) => (
                <li
                  key={campaignId}
                  className={classNames({
                    open: actives.includes(campaignId),
                    on: selectedCampaignId === campaignId,
                  })}>
                  <CampaignLink
                    id={campaignId}
                    campaignType={campaignType}
                    className="link_sublist">
                    <span
                      className={classNames('ico_state', {
                        ico_on: campaignStatus === 'ON',
                        ico_off: campaignStatus !== 'ON',
                      })}
                    />
                    {campaignName} ({adGroupCnt})
                    <span
                      className="ico_gnb ico_arrow"
                      onClick={ev => {
                        handleCampaignToggle(ev, campaignId)
                      }}
                    />
                  </CampaignLink>
                  {actives.includes(campaignId) && (
                    <ul className="list_sub">
                      {adGroupIds.map(adGroupId => {
                        const adGroup = adGroups[adGroupId]
                        if (adGroup) {
                          const {
                            name: adGroupName,
                            status: adGroupStatus,
                            creativeCount,
                            keywordCount,
                            loaded,
                          } = adGroup
                          return (
                            <li
                              key={adGroupId}
                              className={classNames({
                                on: selectedAdgroupId === adGroupId,
                              })}>
                              <AdGroupLink
                                id={adGroupId}
                                campaignId={campaignId}
                                campaignType={campaignType}
                                className="link_sub">
                                <span
                                  className={classNames('ico_state', {
                                    ico_on: adGroupStatus === 'ON',
                                    ico_off: adGroupStatus !== 'ON',
                                  })}
                                />
                                {adGroupName}{' '}
                                {loaded
                                  ? `(${keywordCount + creativeCount})`
                                  : ''}
                              </AdGroupLink>
                            </li>
                          )
                        } else {
                          return null
                        }
                      })}
                    </ul>
                  )}
                </li>
              )
            )}
          </ul>
        )}
      </li>
    </>
  )
}
CampaignList.propTypes = {
  campaignType: PropTypes.oneOf([
    CampaignTypes.PREMIUM_LINK,
    CampaignTypes.TALK_CHANNEL,
  ]),
}
export default CampaignList
